import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { ArrowLeft, KeyRound, Ban, CircleCheck, Clock, UserX, Trash2, RotateCcw, User as UserIcon, Mail, Phone, Shield, MailCheck, MailX, Activity } from 'lucide-react';
import { User } from '../../../../shared/types/user.types';
import { auth } from '../../config/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import useSettingLists from '../../wrappers/use-setting-list-item';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../utils/useDateFormat';
import { accountDetailStyles } from './AccountDetail.styles';

const API_URL = import.meta.env.VITE_API_URL as string

const AccountDetail: React.FC = () => {
    const { uid } = useParams<{ uid: string }>();
    const { t } = useTranslation();
    const formatDate = useDateFormat();
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [dealerMap, setDealerMap] = useState<Record<string, string>>({});
    const settingLists = useSettingLists();

    useEffect(() => {
        settingLists.list('dealers').then((items) => {
            setDealerMap(Object.fromEntries(items.map((i) => [i.id, i.label])));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!uid) return;
        const currentUser = auth.currentUser;
        if (!currentUser) { setLoading(false); return; }
        currentUser.getIdToken().then((token) => {
            fetch(`${API_URL}/api/users/${uid}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
                .then((r) => r.json())
                .then((body: { user?: User }) => {
                    if (body.user) setUser(body.user);
                })
                .catch((err) => { console.error('[AccountDetail] Failed to fetch user:', err); })
                .finally(() => setLoading(false));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid]);

    const setDisabled = async (disabled: boolean) => {
        if (!user) return;
        setActionLoading(true);
        try {
            const token = await auth.currentUser?.getIdToken();
            const res = await fetch(`${API_URL}/api/users/${user.uid}/disabled`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ disabled }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({})) as { message?: string };
                throw new Error(body.message || t('common.operationFailed'));
            }
            setUser((prev) => prev ? { ...prev, disabled } : null);
        } catch (err) {
            console.error(err);
            setMsg({ type: 'error', text: t('common.operationFailed') });
        } finally {
            setActionLoading(false);
        }
    };

    const handleBlockUser = () => setDisabled(true);
    const handleUnblockUser = () => setDisabled(false);

    const setStatus = async (status: string) => {
        if (!user) return;
        setActionLoading(true);
        try {
            const token = await auth.currentUser?.getIdToken();
            const res = await fetch(`${API_URL}/api/users/${user.uid}/status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ status }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({})) as { message?: string };
                throw new Error(body.message || t('common.operationFailed'));
            }
            const freshToken = await auth.currentUser?.getIdToken();
            const refreshed = await fetch(`${API_URL}/api/users/${user.uid}`, {
                headers: { Authorization: `Bearer ${freshToken}` },
            });
            if (refreshed.ok) {
                const data = await refreshed.json() as { user?: User };
                if (data.user) setUser(data.user);
            }
        } catch (err) {
            console.error(err);
            setMsg({ type: 'error', text: t('common.operationFailed') });
        } finally {
            setActionLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (!user?.email) return;
        setActionLoading(true);
        try {
            await sendPasswordResetEmail(auth, user.email);
            setMsg({ type: 'success', text: t('accountDetail.resetPasswordSent', { email: user.email }) });
        } catch (err) {
            console.error('[AccountDetail] Failed to send password reset email:', err);
            setMsg({ type: 'error', text: t('accountDetail.resetPasswordFailed') });
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={accountDetailStyles.loadingContainer}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={accountDetailStyles.notFoundContainer}>
                <Typography variant="h6">{t('accountDetail.notFound')}</Typography>
                <Button component={Link} to="/app/admin/users" startIcon={<ArrowLeft size={20} />}>
                    {t('accountDetail.backToList')}
                </Button>
            </Box>
        );
    }

    const isAccountCreated = (user.providers?.length ?? 0) > 0;
    const authLabel = !isAccountCreated ? t('accountDetail.authNotCreated') : user.disabled ? t('accountDetail.authBlocked') : t('accountDetail.authActive');
    const authColor = !isAccountCreated ? 'text.secondary' : user.disabled ? 'error.main' : 'success.main';
    const authIcon = !isAccountCreated ? <UserX size={16} /> : user.disabled ? <Ban size={16} /> : <CircleCheck size={16} />;

    const infoRows: { icon: React.ReactNode; label: string; value: React.ReactNode; subRows?: { label: string; value: string }[] }[] = [
        { icon: <UserIcon size={16} />, label: t('accountDetail.nameLabel'), value: user.name },
        { icon: <Mail size={16} />, label: t('accountDetail.emailLabel'), value: user.email },
        { icon: <Phone size={16} />, label: t('accountDetail.phoneLabel'), value: user.phone || '–' },
        {
            icon: <Shield size={16} />,
            label: t('accountDetail.roleLabel'),
            value: user.role,
            ...(user.role === 'dealer' && user.dealerId && {
                subRows: [{ label: t('accountDetail.dealerLabel'), value: dealerMap[user.dealerId] ?? user.dealerId }],
            }),
        },
        {
            icon: <Activity size={16} />,
            label: t('accountDetail.accountStatusLabel'),
            value: (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: authColor }}>
                        {authIcon}
                        <Typography variant="body2" color="inherit">{authLabel}</Typography>
                    </Box>
                    {user.status === 'pending' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'warning.main' }}>
                            <Clock size={16} />
                            <Typography variant="body2" color="inherit">{t('accountDetail.pendingActivation')}</Typography>
                        </Box>
                    )}
                    {user.status === 'requestDelete' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'error.main' }}>
                            <Trash2 size={16} />
                            <Typography variant="body2" color="inherit">{t('accountDetail.requestDelete')}</Typography>
                        </Box>
                    )}
                </Box>
            ),
            subRows: [
                ...(user.statusAt ? [{ label: t('accountDetail.statusSetAt'), value: formatDate(user.statusAt) }] : []),
                { label: t('accountDetail.createdAt'), value: formatDate(user.createdAt) },
                { label: t('accountDetail.lastLogin'), value: formatDate(user.lastSignInTime) },
            ],
        },
        {
            icon: user.emailVerified ? <MailCheck size={16} /> : <MailX size={16} />,
            label: t('accountDetail.emailVerifiedLabel'),
            value: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: user.emailVerified ? 'success.main' : 'error.main' }}>
                    {user.emailVerified ? <CircleCheck size={16} /> : <Ban size={16} />}
                    <Typography variant="body2" color="inherit">{user.emailVerified ? t('accountDetail.emailVerifiedYes') : t('accountDetail.emailVerifiedNo')}</Typography>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={accountDetailStyles.root}>
            <Box sx={accountDetailStyles.header}>
                <Box sx={accountDetailStyles.headerLeft}>
                    <IconButton component={Link} to="/app/admin/users" size="small">
                        <ArrowLeft size={20} />
                    </IconButton>
                    <Typography variant="h4" fontWeight={700}>{t('accountDetail.title', { name: user.name })}</Typography>
                </Box>
                <Box sx={accountDetailStyles.headerActions}>
                    {user.providers?.includes('password') && (
                        <Button variant="outlined" startIcon={<KeyRound size={20} />} onClick={handleResetPassword} disabled={actionLoading}>
                            {t('accountDetail.resetPassword')}
                        </Button>
                    )}
                    {user.status !== 'requestDelete' && (
                        <Button variant="outlined" color="error" startIcon={<Trash2 size={20} />} onClick={() => setStatus('requestDelete')} disabled={actionLoading}>
                            {t('accountDetail.deleteAccount')}
                        </Button>
                    )}
                    {user.status === 'requestDelete' && (
                        <Button variant="outlined" color="warning" startIcon={<RotateCcw size={20} />} onClick={() => setStatus('')} disabled={actionLoading}>
                            {t('accountDetail.cancelDeletion')}
                        </Button>
                    )}
                    {isAccountCreated && (!user.disabled ? (
                        <Button variant="contained" color="error" startIcon={<Ban size={20} />} onClick={handleBlockUser} disabled={actionLoading}>
                            {t('accountDetail.block')}
                        </Button>
                    ) : (
                        <Button variant="contained" color="success" startIcon={<CircleCheck size={20} />} onClick={handleUnblockUser} disabled={actionLoading}>
                            {t('accountDetail.activate')}
                        </Button>
                    ))}
                </Box>
            </Box>

            {msg && (
                <Alert severity={msg.type} onClose={() => setMsg(null)} sx={{ mb: 2 }}>
                    {msg.text}
                </Alert>
            )}

            <Paper elevation={0} sx={accountDetailStyles.card}>
                <Box sx={accountDetailStyles.cardHeader}>
                    <Typography variant="subtitle1" fontWeight={600}>{t('accountDetail.sectionTitle')}</Typography>
                </Box>
                {infoRows.map((row, i) => (
                    <Box key={row.label as string}>
                        <Box sx={accountDetailStyles.infoRow}>
                            <Typography variant="body2" color="text.secondary" fontWeight={500} sx={accountDetailStyles.infoLabel}>
                                {row.icon} {row.label}
                            </Typography>
                            <Box>{row.value}</Box>
                        </Box>
                        {row.subRows?.map((sub) => (
                            <Box key={sub.label} sx={{ ...accountDetailStyles.infoRow, py: 0.75, opacity: 0.75 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ pl: '20px' }}>
                                    {sub.label}
                                </Typography>
                                <Typography variant="caption">{sub.value}</Typography>
                            </Box>
                        ))}
                        {i < infoRows.length - 1 && <Divider />}
                    </Box>
                ))}
            </Paper>
        </Box>
    );
};

export default AccountDetail;
